import React, { useState } from "react";
import { analyzeNewWordAI, createAdminWord } from "../api";

const POS_OPTIONS = [
  { value: "noun", label: "Noun" },
  { value: "verb", label: "Verb" },
  { value: "particle", label: "Particle" },
];

const VERB_FORM_LABELS = {
  past: "Past (الماضي)",
  present: "Present (المضارع)",
  imperative: "Command (الأمر)",
  masdar: "Maṣdar (المصدر)",
  ism_faail: "Ism fāʿil (اسم الفاعل)",
  ism_mafool: "Ism mafʿūl (اسم المفعول)",
};

/**
 * Admin-only modal to add a brand-new word that doesn't exist yet (opened from
 * the search bar when a search returns nothing). Mirrors the Word Bank
 * add-word form, plus a one-tap "Fetch details (AI)" that uses the existing
 * OpenAI integration to pull the definition and — for nouns the plural, for
 * verbs the full paradigm + conjugation table — which are saved and linked.
 *
 * On success (or when the word already exists) calls onCreated(word, message).
 */
export default function AddWordModal({ initialText = "", onClose, onCreated }) {
  const [text, setText] = useState(initialText);
  const [pos, setPos] = useState("noun");
  const [definition, setDefinition] = useState("");
  const [transliteration, setTransliteration] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const forms = analysis?.forms || {};
  const verbForms = Object.entries(VERB_FORM_LABELS)
    .filter(([k]) => (forms[k] || "").trim())
    .map(([k, label]) => ({ key: k, label, value: forms[k] }));

  const fetchAI = async () => {
    if (!text.trim()) return;
    setFetching(true);
    setError(null);
    try {
      const r = await analyzeNewWordAI(text.trim());
      const a = r.analysis || {};
      setAnalysis(a);
      setDefinition(a.definition || "");
      if (r.pos) setPos(r.pos);
      if ((a.matched_word || "").trim()) setText(a.matched_word.trim());
    } catch (e) {
      setError(e.message || "Could not analyze the word.");
    } finally {
      setFetching(false);
    }
  };

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        text: text.trim(),
        pos,
        definition: definition.trim(),
        transliteration: transliteration.trim(),
      };
      if (analysis) {
        payload.analysis = { ...analysis, definition: definition.trim() || analysis.definition };
      }
      const res = await createAdminWord(payload);
      onCreated(res.word, res.message);
    } catch (e) {
      if (e.status === 409 && e.word) {
        onCreated(e.word, "That word already exists — opening it.");
        return;
      }
      setError(e.message || "Could not create the word.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-ink-900/40" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-parchment-300 p-5 space-y-3 max-h-[90vh] overflow-y-auto scroll-elegant"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink-800">Add a new word</h2>
          <button onClick={onClose} className="p-1 rounded text-slate-400 hover:bg-slate-100" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <label className="block text-xs font-medium text-ink-500">Arabic word</label>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          dir="rtl"
          placeholder="اكتب الكلمة"
          className="w-full px-3 py-2 rounded-xl border border-parchment-300 text-lg text-ink-800 focus:outline-none focus:ring-2 focus:ring-ink-400"
        />

        <button
          onClick={fetchAI}
          disabled={fetching || !text.trim()}
          className="w-full px-3 py-2 rounded-xl border border-violet-300 text-violet-700 hover:bg-violet-50 text-sm font-medium disabled:opacity-50"
        >
          {fetching ? "Fetching…" : "Fetch details (AI)"}
        </button>
        <p className="text-[11px] text-ink-400 -mt-1">
          Auto-detects noun/verb, defines it, and pulls the plural (nouns) or the full paradigm &amp; conjugations (verbs).
        </p>

        <div>
          <label className="block text-xs font-medium text-ink-500 mb-1">Meaning</label>
          <input
            value={definition}
            onChange={(e) => setDefinition(e.target.value)}
            placeholder="English meaning"
            className="w-full px-3 py-2 rounded-xl border border-parchment-300 text-sm focus:outline-none focus:ring-2 focus:ring-ink-400"
          />
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-ink-500 mb-1">Part of speech</label>
            <select
              value={pos}
              onChange={(e) => setPos(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-parchment-300 text-sm focus:outline-none focus:ring-2 focus:ring-ink-400"
            >
              {POS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-ink-500 mb-1">Transliteration</label>
            <input
              value={transliteration}
              onChange={(e) => setTransliteration(e.target.value)}
              placeholder="optional"
              className="w-full px-3 py-2 rounded-xl border border-parchment-300 text-sm focus:outline-none focus:ring-2 focus:ring-ink-400"
            />
          </div>
        </div>

        {analysis && (pos === "verb" ? verbForms.length > 0 : (forms.plural || "").trim()) && (
          <div className="rounded-xl border border-parchment-300 bg-parchment-50 p-3 space-y-1.5">
            <p className="text-xs font-semibold text-ink-500">Will be saved &amp; linked</p>
            {pos === "verb" ? (
              <>
                <ul className="space-y-1">
                  {verbForms.map((f) => (
                    <li key={f.key} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-ink-400">{f.label}</span>
                      <span className="font-arabic text-ink-800" dir="rtl">{f.value}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-ink-400 pt-1">
                  {analysis.conjugations?.length || 0} conjugation form(s)
                  {analysis.particles?.length ? `, ${analysis.particles.length} particle(s)` : ""} will be added.
                </p>
              </>
            ) : (
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-ink-400">Plural</span>
                <span className="font-arabic text-ink-800" dir="rtl">{forms.plural}</span>
              </div>
            )}
          </div>
        )}

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 rounded-xl border border-parchment-300 text-sm text-ink-600 hover:bg-parchment-100"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !text.trim()}
            className="flex-1 px-3 py-2 rounded-xl bg-ink-600 hover:bg-ink-700 text-white text-sm font-semibold disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save word"}
          </button>
        </div>
        <p className="text-[11px] text-ink-400">
          Saved words are public — searchable by everyone and auto-linked into scenario texts.
        </p>
      </div>
    </div>
  );
}
